import {
  Banner,
  BlockStack,
  Button,
  Card,
  Collapsible,
  DataTable,
  InlineStack,
  ProgressBar,
  Select,
  Text,
} from "@shopify/polaris";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  downloadStockyPortabilityDatasetChunk,
  downloadStockyPortabilityZip,
  downloadStockySourceArchive,
  downloadStockySourceGenericRowsChunk,
  listStockyPortabilityDatasets,
  listStockySourceArchives,
  localPortabilityIntegrityError,
  uploadStockySourceArchive,
  verifyStockySourceGenericRows,
  verifyStockyPortabilityDataset,
  type DownloadedFile,
  type StockyArchiveOccurrence,
  type StockyGenericParseOptions,
  type StockyPortabilityDataset,
  type StockySourceArchive,
} from "../../api/stocky_portability";
import { ApiError } from "../../api/client";
import {
  canonicalStockyPublicBigIntCursor,
  isStockyPublicBigIntId,
} from "../../api/stocky_public_ids";
import { csvErrorMessage } from "../../components/csvImportSpec";
import { useAuthoritativeShop } from "../../hooks/useAuthoritativeShop";
import { t } from "../../lib/i18n";
import { formatDateTime } from "../../utils/date";
import { formatNumber } from "../../utils/money";

const ARCHIVE_CATEGORIES = [
  { value: "stock_on_hand", label: "Stock on hand（在庫スナップショット）" },
  {
    value: "stocky_low_stock_csv",
    label: "Low Stock（専用取込で5MiBを超えた原本）",
  },
  {
    value: "manual_suppliers_csv",
    label: "手動仕入先マスタ CSV（0件・運用上限超原本）",
  },
  {
    value: "manual_supplier_links_csv",
    label: "手動商品・仕入先リンク CSV（0件・運用上限超原本）",
  },
  { value: "average_cost", label: "Average cost（平均原価）" },
  { value: "purchase_order_parent_list", label: "Purchase Orders（親一覧）" },
  { value: "purchase_order_detail", label: "Purchase Order（個別発注書）" },
  { value: "stock_adjustments", label: "Adjustments（在庫調整）" },
] as const;

const ARCHIVE_CATEGORY_GUIDE: Record<
  (typeof ARCHIVE_CATEGORIES)[number]["value"],
  { path: string; file: string }
> = {
  stock_on_hand: {
    path: "Reports → Stock on hand → CSV",
    file: "stock_on_hand.csv",
  },
  stocky_low_stock_csv: {
    path: "Reports → Low Stock → SKU/Variants → CSV",
    file: "low_stock_csv.csv（専用欄の上限を超えた場合だけ）",
  },
  manual_suppliers_csv: {
    path: "Stocky公式CSVなし。上の仕入先欄で作成したCSVを選びます。",
    file: "suppliers.csv",
  },
  manual_supplier_links_csv: {
    path: "Stocky公式CSVなし。上の商品と仕入先の紐付け欄で作成したCSVを選びます。",
    file: "supplier_links.csv",
  },
  average_cost: {
    path: "Reports → Stock on handでAverage costを選択 → CSV",
    file: "stock_on_hand.csv（Average costを選んで書き出したもの）",
  },
  purchase_order_parent_list: {
    path: "Reports → Purchase Orders → CSV",
    file: "purchase_orders.csv",
  },
  purchase_order_detail: {
    path: "Purchases → 対象の発注書 → Download → CSV",
    file: "purchase_order_<PO番号>.csv",
  },
  stock_adjustments: {
    path: "Reports → Adjustments → CSV",
    file: "stock_adjustments.csv",
  },
};

const DOWNLOAD_URL_REVOKE_DELAY_MS = 60_000;
const MAX_PENDING_DOWNLOAD_URLS = 32;
const MAX_HEADER_SOURCE_BYTES = 25 * 1024 * 1024;
const HEADER_READ_CHUNK_BYTES = 64 * 1024;
// Presets are optional browser convenience only.  Do not retain an unbounded
// logical record merely to calculate a local header signature.
const MAX_HEADER_SIGNATURE_RECORD_BYTES = 128 * 1024;
const headerTextEncoder = new TextEncoder();

export class BrowserDownloadRegistry {
  private readonly pending = new Map<string, number>();
  private closed = false;

  handoff(download: DownloadedFile): boolean {
    if (this.closed) return false;
    while (this.pending.size >= MAX_PENDING_DOWNLOAD_URLS) {
      const oldest = this.pending.keys().next().value as string | undefined;
      if (oldest === undefined) break;
      this.revoke(oldest);
    }
    const url = URL.createObjectURL(download.blob);
    try {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = download.filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // A synchronous event handler reached from click() may tear down the
      // page before control returns here.  Do not resurrect an object URL or
      // report a successful handoff after that close.
      if (this.closed) {
        URL.revokeObjectURL(url);
        return false;
      }
      const timeout = window.setTimeout(
        () => this.revoke(url),
        DOWNLOAD_URL_REVOKE_DELAY_MS,
      );
      this.pending.set(url, timeout);
      return true;
    } catch (error) {
      URL.revokeObjectURL(url);
      throw error;
    }
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    for (const url of [...this.pending.keys()]) this.revoke(url);
  }

  private revoke(url: string): void {
    const timeout = this.pending.get(url);
    if (timeout === undefined) return;
    window.clearTimeout(timeout);
    this.pending.delete(url);
    URL.revokeObjectURL(url);
  }
}

function portabilityDownloadError(error: unknown): string {
  if (error instanceof ApiError && error.status === 413) {
    return t(
      "一括ZIPの上限（64MBまたは原本件数）を超えました。データは削除されていません。下の正規化データセットを検証完了までチャンク取得し、保存済み原本は一覧から個別にダウンロードしてください。",
    );
  }
  if (error instanceof ApiError && error.status === 429) {
    return `${t(
      "別のデータ書き出しを処理中です。表示された再試行時間を待ってから、同じ操作をもう一度お試しください。",
    )} ${error.detail}`;
  }
  if (error instanceof ApiError && error.status === 507) {
    return `${t(
      "一時保存先の空き容量を確保できませんでした。データは削除されていません。表示された再試行時間を待ってから、同じ操作をもう一度お試しください。",
    )} ${error.detail}`;
  }
  if (error instanceof ApiError && error.status === 409) {
    return t(
      "チャンク取得中にデータが変更されました。保存済みファイルは残りますが、完全な一式とは確認できません。最初のチャンクから再取得してください。",
    );
  }
  return csvErrorMessage(error);
}

interface DatasetProgress {
  cursor: string | null;
  chunkCount: number;
  recordCount: number;
  highWatermark: string | null;
  verified: boolean;
  completeCandidate: boolean;
  browserFilesConfirmed: boolean;
  filenames: string[];
  orderedRoot: string | null;
  expiresAt: number | null;
}

interface StoredDatasetProgress {
  cursor: string;
  cumulative_record_count: number;
  part_sequence: number;
  filenames: string[];
  expires_at: number;
  complete_candidate: boolean;
}

const DATASET_PROGRESS_STORAGE_PREFIX = "ureyukibox:stocky-dataset:v1";

interface GenericRowProgress {
  sourceSha256: string | null;
  cursor: string | null;
  partCount: number;
  recordCount: number;
  highWatermark: number | null;
  verified: boolean;
  completeCandidate: boolean;
  browserFilesConfirmed: boolean;
  filenames: string[];
  orderedRoot: string | null;
  expiresAt: number | null;
}

interface PortabilityVerificationOwner {
  token: symbol;
  kind: "dataset" | "generic";
  resourceKey: string;
  shopId: string;
  shopGeneration: string;
  cursor: string;
  orderedRoot: string | null;
  sourceSha256: string | null;
}

interface StoredGenericRowProgress {
  cursor: string;
  part_sequence: number;
  cumulative_record_count: number;
  high_watermark: number;
  filenames: string[];
  complete_candidate: boolean;
  ordered_root: string;
  expires_at: number;
}

const GENERIC_ROW_PROGRESS_STORAGE_PREFIX = "ureyukibox:stocky-generic-row:v2";

function emptyDatasetProgress(): DatasetProgress {
  return {
    cursor: null,
    chunkCount: 0,
    recordCount: 0,
    highWatermark: null,
    verified: false,
    completeCandidate: false,
    browserFilesConfirmed: false,
    filenames: [],
    orderedRoot: null,
    expiresAt: null,
  };
}

function compareCanonicalBigIntStrings(left: string, right: string): number {
  const leftValue = BigInt(left);
  const rightValue = BigInt(right);
  return leftValue < rightValue ? -1 : leftValue > rightValue ? 1 : 0;
}

function datasetProgressStoragePrefix(shopId: string, dataset: string): string {
  return `${DATASET_PROGRESS_STORAGE_PREFIX}:${shopId}:${dataset}:`;
}

function datasetProgressStorageKey(
  shopId: string,
  dataset: string,
  highWatermark: string,
  orderedRoot: string,
): string {
  return `${datasetProgressStoragePrefix(shopId, dataset)}${highWatermark}:${orderedRoot}`;
}

function removeStoredDatasetProgress(shopId: string, dataset: string): void {
  const prefix = datasetProgressStoragePrefix(shopId, dataset);
  try {
    const keys = Array.from({ length: localStorage.length }, (_, index) =>
      localStorage.key(index),
    ).filter((key): key is string => key !== null && key.startsWith(prefix));
    for (const key of keys) localStorage.removeItem(key);
  } catch {
    // The signed cursor remains the authority when local storage is unavailable.
  }
}

function persistDatasetProgress(
  shopId: string,
  dataset: string,
  progress: DatasetProgress,
): void {
  if (
    progress.cursor === null ||
    progress.chunkCount < 1 ||
    progress.highWatermark === null ||
    progress.orderedRoot === null ||
    progress.expiresAt === null
  ) {
    removeStoredDatasetProgress(shopId, dataset);
    return;
  }
  const stored: StoredDatasetProgress = {
    cursor: progress.cursor,
    cumulative_record_count: progress.recordCount,
    part_sequence: progress.chunkCount,
    filenames: progress.filenames,
    expires_at: progress.expiresAt,
    complete_candidate: progress.completeCandidate,
  };
  const encoded = JSON.stringify(stored);
  if (progress.filenames.length > 10_000 || encoded.length > 1024 * 1024) {
    removeStoredDatasetProgress(shopId, dataset);
    return;
  }
  try {
    removeStoredDatasetProgress(shopId, dataset);
    localStorage.setItem(
      datasetProgressStorageKey(
        shopId,
        dataset,
        progress.highWatermark,
        progress.orderedRoot,
      ),
      encoded,
    );
  } catch {
    removeStoredDatasetProgress(shopId, dataset);
  }
}

function loadStoredDatasetProgress(
  shopId: string,
  dataset: string,
): DatasetProgress | null {
  const prefix = datasetProgressStoragePrefix(shopId, dataset);
  try {
    const matchingKeys = Array.from(
      { length: localStorage.length },
      (_, index) => localStorage.key(index),
    ).filter((key): key is string => key !== null && key.startsWith(prefix));
    if (matchingKeys.length !== 1) {
      removeStoredDatasetProgress(shopId, dataset);
      return null;
    }
    const key = matchingKeys[0];
    const suffix = key.slice(prefix.length);
    const separator = suffix.indexOf(":");
    if (separator < 1) throw new Error("invalid dataset progress key");
    const highWatermark = canonicalStockyPublicBigIntCursor(
      suffix.slice(0, separator),
    );
    const orderedRoot = suffix.slice(separator + 1);
    const raw = localStorage.getItem(key);
    if (raw === null || raw.length > 1024 * 1024) {
      throw new Error("invalid dataset progress value");
    }
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      throw new Error("invalid dataset progress value");
    }
    const value = parsed as Record<string, unknown>;
    const keys = Object.keys(value).sort();
    const expectedKeys = [
      "complete_candidate",
      "cumulative_record_count",
      "cursor",
      "expires_at",
      "filenames",
      "part_sequence",
    ];
    if (
      keys.length !== expectedKeys.length ||
      keys.some((item, index) => item !== expectedKeys[index]) ||
      !/^[a-f0-9]{64}$/.test(orderedRoot) ||
      typeof value.cursor !== "string" ||
      value.cursor.length < 1 ||
      value.cursor.length > 8_192 ||
      !Number.isSafeInteger(value.cumulative_record_count) ||
      Number(value.cumulative_record_count) < 0 ||
      !Number.isSafeInteger(value.part_sequence) ||
      Number(value.part_sequence) < 1 ||
      !Array.isArray(value.filenames) ||
      value.filenames.length > 10_000 ||
      value.filenames.some(
        (filename) => typeof filename !== "string" || filename.length > 512,
      ) ||
      !Number.isSafeInteger(value.expires_at) ||
      Number(value.expires_at) <= Math.floor(Date.now() / 1000) ||
      typeof value.complete_candidate !== "boolean"
    ) {
      throw new Error("invalid dataset progress value");
    }
    return {
      cursor: value.cursor,
      chunkCount: Number(value.part_sequence),
      recordCount: Number(value.cumulative_record_count),
      highWatermark,
      verified: false,
      completeCandidate: value.complete_candidate,
      browserFilesConfirmed: false,
      filenames: [...value.filenames] as string[],
      orderedRoot,
      expiresAt: Number(value.expires_at),
    };
  } catch {
    removeStoredDatasetProgress(shopId, dataset);
    return null;
  }
}

function genericRowProgressStorageKey(
  shopId: string,
  archive: Pick<StockySourceArchive, "id" | "sha256">,
): string {
  return `${GENERIC_ROW_PROGRESS_STORAGE_PREFIX}:${shopId}:${archive.id}:${archive.sha256}`;
}

function removeStoredGenericRowProgress(
  shopId: string,
  archive: Pick<StockySourceArchive, "id" | "sha256">,
): void {
  try {
    localStorage.removeItem(genericRowProgressStorageKey(shopId, archive));
  } catch {
    // Storage is only a resume convenience; the signed server cursor remains authoritative.
  }
}

function removeStoredGenericRowProgressForOtherSource(
  shopId: string,
  archive: Pick<StockySourceArchive, "id" | "sha256">,
): void {
  const expected = genericRowProgressStorageKey(shopId, archive);
  const archivePrefix = `${GENERIC_ROW_PROGRESS_STORAGE_PREFIX}:${shopId}:${archive.id}:`;
  try {
    const keys = Array.from({ length: localStorage.length }, (_, index) =>
      localStorage.key(index),
    ).filter(
      (key): key is string =>
        key !== null && key.startsWith(archivePrefix) && key !== expected,
    );
    for (const key of keys) localStorage.removeItem(key);
  } catch {
    // The current source SHA is still checked by every server response.
  }
}

function persistGenericRowProgress(
  shopId: string,
  archive: Pick<StockySourceArchive, "id" | "sha256">,
  progress: GenericRowProgress,
): void {
  if (
    progress.cursor === null ||
    progress.partCount < 1 ||
    progress.expiresAt === null
  ) {
    removeStoredGenericRowProgress(shopId, archive);
    return;
  }
  const stored: StoredGenericRowProgress = {
    cursor: progress.cursor,
    part_sequence: progress.partCount,
    cumulative_record_count: progress.recordCount,
    high_watermark: progress.highWatermark ?? -1,
    filenames: progress.filenames,
    complete_candidate: progress.completeCandidate,
    ordered_root: progress.orderedRoot ?? "",
    expires_at: progress.expiresAt,
  };
  const encoded = JSON.stringify(stored);
  if (
    progress.highWatermark === null ||
    progress.filenames.length !== progress.partCount ||
    progress.filenames.length > 10_000 ||
    encoded.length > 1024 * 1024
  ) {
    removeStoredGenericRowProgress(shopId, archive);
    return;
  }
  try {
    localStorage.setItem(
      genericRowProgressStorageKey(shopId, archive),
      encoded,
    );
  } catch {
    // A failed local save must not turn a verified download into a failed response.
  }
}

function loadStoredGenericRowProgress(
  shopId: string,
  archive: Pick<StockySourceArchive, "id" | "sha256">,
): GenericRowProgress | null {
  const key = genericRowProgressStorageKey(shopId, archive);
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      throw new Error("invalid stored generic progress");
    }
    const value = parsed as Record<string, unknown>;
    const keys = Object.keys(value).sort();
    const expectedKeys = [
      "complete_candidate",
      "cumulative_record_count",
      "cursor",
      "expires_at",
      "filenames",
      "high_watermark",
      "ordered_root",
      "part_sequence",
    ];
    if (
      keys.length !== expectedKeys.length ||
      keys.some((item, index) => item !== expectedKeys[index]) ||
      typeof value.cursor !== "string" ||
      value.cursor.length < 1 ||
      value.cursor.length > 8_192 ||
      !Number.isSafeInteger(value.part_sequence) ||
      Number(value.part_sequence) < 1 ||
      !Number.isSafeInteger(value.cumulative_record_count) ||
      Number(value.cumulative_record_count) < 0 ||
      !Number.isSafeInteger(value.high_watermark) ||
      Number(value.high_watermark) < 0 ||
      !Array.isArray(value.filenames) ||
      value.filenames.length !== Number(value.part_sequence) ||
      value.filenames.length > 10_000 ||
      value.filenames.some(
        (filename) => typeof filename !== "string" || filename.length > 512,
      ) ||
      typeof value.complete_candidate !== "boolean" ||
      typeof value.ordered_root !== "string" ||
      !/^[a-f0-9]{64}$/.test(value.ordered_root) ||
      !Number.isSafeInteger(value.expires_at) ||
      Number(value.expires_at) <= Math.floor(Date.now() / 1000)
    ) {
      throw new Error("invalid stored generic progress");
    }
    return {
      sourceSha256: archive.sha256,
      cursor: value.cursor,
      partCount: Number(value.part_sequence),
      recordCount: Number(value.cumulative_record_count),
      highWatermark: Number(value.high_watermark),
      verified: false,
      completeCandidate: value.complete_candidate,
      browserFilesConfirmed: false,
      filenames: [...value.filenames] as string[],
      orderedRoot: value.ordered_root,
      expiresAt: Number(value.expires_at),
    };
  } catch {
    try {
      localStorage.removeItem(key);
    } catch {
      // No persistent state is safer than accepting a malformed cursor envelope.
    }
    return null;
  }
}

function emptyGenericRowProgress(): GenericRowProgress {
  return {
    sourceSha256: null,
    cursor: null,
    partCount: 0,
    recordCount: 0,
    highWatermark: null,
    verified: false,
    completeCandidate: false,
    browserFilesConfirmed: false,
    filenames: [],
    orderedRoot: null,
    expiresAt: null,
  };
}

function genericRowProgressLabel(progress: GenericRowProgress): string {
  if (progress.browserFilesConfirmed) {
    return t(
      "汎用JSONL: 検証済み・ユーザー確認済み（{count} 行 / {parts} パート）",
      { count: progress.recordCount, parts: progress.partCount },
    );
  }
  if (progress.verified) {
    return t(
      "汎用JSONL: 検証済み・ブラウザ保存未確認（{count} 行 / {parts} パート）",
      { count: progress.recordCount, parts: progress.partCount },
    );
  }
  if (progress.partCount > 0) {
    return t(
      "汎用JSONL: {parts} パート / {count} of {total} 行を受信（保存未確認）",
      {
        parts: progress.partCount,
        count: progress.recordCount,
        total: progress.highWatermark ?? "—",
      },
    );
  }
  return t("汎用JSONL: 未受信");
}

function genericRowButtonLabel(
  progress: GenericRowProgress,
  verificationInFlight = false,
): string {
  if (progress.browserFilesConfirmed) return t("汎用JSONLを最初から再取得");
  if (progress.verified) return t("ダウンロード一覧で汎用JSONLを確認した");
  if (verificationInFlight) return t("最終整合性を検証中");
  if (progress.completeCandidate) return t("検証を再試行");
  if (progress.partCount > 0) return t("次の汎用JSONLチャンクを取得");
  return t("最初の汎用JSONLチャンクを取得");
}

function newOccurrence(file: File): StockyArchiveOccurrence {
  return { file, sourceImportId: globalThis.crypto.randomUUID() };
}

function parseColumnMappings(value: string): Record<string, string> | null {
  const mappings: Record<string, string> = {};
  const labels = new Set<string>();
  for (const rawLine of value.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const match = line.match(/^(\d+)\s*=\s*(\S(?:.*\S)?)$/);
    if (!match) return null;
    const position = Number(match[1]);
    const label = match[2];
    if (
      !Number.isSafeInteger(position) ||
      position < 1 ||
      position > 10_000 ||
      label.length > 128 ||
      [...label].some(
        (character) => character.codePointAt(0)! < 32 || character === "\u007f",
      ) ||
      labels.has(label) ||
      Object.keys(mappings).length >= 512
    ) {
      return null;
    }
    const key = `column_${position}`;
    if (mappings[key] !== undefined) return null;
    mappings[key] = label;
    labels.add(label);
  }
  return mappings;
}

function genericUnavailableLabel(reason: string | null): string {
  if (reason === "encoding_or_text_parse_failed") {
    return t("文字コードを安全に解析できないため、原本のみ取得できます");
  }
  if (reason === "generic_column_limit_exceeded") {
    return t("列数が安全上限を超えるため、原本のみ取得できます");
  }
  if (
    reason === "generic_row_limit_exceeded" ||
    reason === "generic_rectangular_cell_limit_exceeded"
  ) {
    return t("CSV形状が安全上限を超えるため、原本のみ取得できます");
  }
  return t("CSV構造を安全に解析できないため、原本のみ取得できます");
}

function formatBytes(value: number): string {
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MiB`;
  if (value >= 1024) return `${(value / 1024).toFixed(1)} KiB`;
  return `${formatNumber(value)} bytes`;
}

function mappingTextFromOptions(options: StockyGenericParseOptions): string {
  return Object.entries(options.column_mappings)
    .sort(([left], [right]) => Number(left.slice(7)) - Number(right.slice(7)))
    .map(([key, value]) => `${key.slice(7)}=${value}`)
    .join("\n");
}

const HEADER_DELIMITERS: Record<
  Exclude<StockyGenericParseOptions["delimiter"], "auto">,
  string
> = {
  comma: ",",
  tab: "\t",
  semicolon: ";",
  pipe: "|",
};

interface HeaderRecordScan {
  currentRecord: number;
  inQuotes: boolean;
  afterQuote: boolean;
  atFieldStart: boolean;
  invalid: boolean;
  complete: boolean;
  parts: string[];
  recordBytes: number;
}

function scanHeaderRecordChunk(
  state: HeaderRecordScan,
  chunk: string,
  targetIndex: number,
  delimiterName: StockyGenericParseOptions["delimiter"],
): void {
  if (state.invalid || state.complete) return;
  const delimiter =
    delimiterName === "auto" ? null : HEADER_DELIMITERS[delimiterName];
  let captureStart = state.currentRecord === targetIndex ? 0 : null;
  const appendCaptured = (end: number) => {
    if (captureStart === null || end <= captureStart) return;
    const value = chunk.slice(captureStart, end);
    const nextBytes =
      state.recordBytes + headerTextEncoder.encode(value).byteLength;
    if (nextBytes > MAX_HEADER_SIGNATURE_RECORD_BYTES) {
      state.invalid = true;
      return;
    }
    state.parts.push(value);
    state.recordBytes = nextBytes;
  };
  const finishCapture = (end: number) => {
    appendCaptured(end);
    captureStart = null;
  };
  for (let index = 0; index < chunk.length; index += 1) {
    const character = chunk[index];
    if (delimiter === null && character === '"') {
      // Backend auto-sniffing is authoritative. A browser-only preset must not
      // guess its logical row boundary when quoting can span physical lines.
      state.invalid = true;
      return;
    }
    if (state.inQuotes) {
      if (!state.afterQuote) {
        if (character === '"') state.afterQuote = true;
        continue;
      }
      if (character === '"') {
        state.afterQuote = false;
        continue;
      }
      state.inQuotes = false;
      state.afterQuote = false;
      if (character !== delimiter && character !== "\r" && character !== "\n") {
        state.invalid = true;
        return;
      }
      // Re-process the delimiter/newline outside the quoted field.
    }
    if (delimiter !== null && character === '"' && state.atFieldStart) {
      state.inQuotes = true;
      state.atFieldStart = false;
      continue;
    }
    if (character === "\n") {
      if (state.currentRecord === targetIndex) {
        finishCapture(index);
        state.complete = true;
        return;
      }
      state.currentRecord += 1;
      state.atFieldStart = true;
      if (state.currentRecord === targetIndex) captureStart = index + 1;
      continue;
    }
    if (delimiter !== null && character === delimiter) {
      state.atFieldStart = true;
    } else if (character !== "\r") {
      state.atFieldStart = false;
    }
  }
  finishCapture(chunk.length);
}

function completedHeaderRecord(
  state: HeaderRecordScan,
  targetIndex: number,
): string {
  if (
    state.invalid ||
    (state.inQuotes && !state.afterQuote) ||
    (!state.complete && state.currentRecord !== targetIndex)
  ) {
    return "";
  }
  const record = state.parts.join("");
  return record.endsWith("\r") ? record.slice(0, -1) : record;
}

function newHeaderRecordScan(): HeaderRecordScan {
  return {
    currentRecord: 0,
    inQuotes: false,
    afterQuote: false,
    atFieldStart: true,
    invalid: false,
    complete: false,
    parts: [],
    recordBytes: 0,
  };
}

/** Reject blank labels including CSV-valid quoted empty fields such as `"",""`. */
function hasMeaningfulLogicalHeader(
  record: string,
  delimiterName: StockyGenericParseOptions["delimiter"],
): boolean {
  const delimiter =
    delimiterName === "auto" ? null : HEADER_DELIMITERS[delimiterName];
  if (delimiter === null) return record.trim().length > 0;

  let inQuotes = false;
  let afterQuote = false;
  let atFieldStart = true;
  for (const character of record) {
    if (inQuotes) {
      if (!afterQuote) {
        if (character === '"') afterQuote = true;
        else if (!/\s/.test(character)) return true;
        continue;
      }
      if (character === '"') {
        // An escaped quote is an actual non-blank field value.
        return true;
      }
      inQuotes = false;
      afterQuote = false;
    }
    if (character === '"' && atFieldStart) {
      inQuotes = true;
      atFieldStart = false;
    } else if (character === delimiter) {
      atFieldStart = true;
    } else if (!/\s/.test(character)) {
      return true;
    } else {
      atFieldStart = false;
    }
  }
  return false;
}

function logicalHeaderRecordAt(
  text: string,
  targetIndex: number,
  delimiter: StockyGenericParseOptions["delimiter"],
): string {
  const state = newHeaderRecordScan();
  scanHeaderRecordChunk(state, text, targetIndex, delimiter);
  const record = completedHeaderRecord(state, targetIndex);
  return hasMeaningfulLogicalHeader(record, delimiter) ? record : "";
}

function readBlobArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  const native = (
    blob as Blob & {
      arrayBuffer?: () => Promise<ArrayBuffer>;
    }
  ).arrayBuffer;
  if (typeof native === "function") return native.call(blob);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(reader.error ?? new Error("blob read failed"));
    reader.onabort = () => reject(new Error("blob read aborted"));
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) resolve(reader.result);
      else reject(new Error("blob read returned an unexpected result"));
    };
    reader.readAsArrayBuffer(blob);
  });
}

async function readLogicalHeaderRecord(
  file: File,
  targetIndex: number,
  delimiter: StockyGenericParseOptions["delimiter"],
  cancelled: () => boolean,
): Promise<string> {
  if (
    !Number.isSafeInteger(targetIndex) ||
    targetIndex < 0 ||
    file.size > MAX_HEADER_SOURCE_BYTES
  ) {
    return "";
  }
  const decoder = new TextDecoder("utf-8", {
    fatal: true,
    ignoreBOM: false,
  });
  const state = newHeaderRecordScan();
  for (let offset = 0; offset < file.size; offset += HEADER_READ_CHUNK_BYTES) {
    if (cancelled()) return "";
    const end = Math.min(file.size, offset + HEADER_READ_CHUNK_BYTES);
    const bytes = await readBlobArrayBuffer(file.slice(offset, end));
    if (cancelled()) return "";
    const decoded = decoder.decode(bytes, { stream: end < file.size });
    scanHeaderRecordChunk(state, decoded, targetIndex, delimiter);
    if (state.invalid || state.complete) break;
  }
  const record = completedHeaderRecord(state, targetIndex);
  return hasMeaningfulLogicalHeader(record, delimiter) ? record : "";
}

// Kept private for the archive reader's compatibility tests; normal UI no
// longer exposes parser overrides.
void parseColumnMappings;
void mappingTextFromOptions;
void logicalHeaderRecordAt;
void readLogicalHeaderRecord;

interface StockyPortabilityProps {
  /** テスト・静的プレビュー用。通常画面では詳細な外部バックアップを閉じる。 */
  externalBackupInitiallyOpen?: boolean;
}

/** Freeで使える元ファイル保管・個別download・全データZIP export。 */
export function StockyPortability({
  externalBackupInitiallyOpen = false,
}: StockyPortabilityProps = {}) {
  const downloadRegistryRef = useRef<BrowserDownloadRegistry | null>(null);
  const saveBrowserDownload = useCallback((download: DownloadedFile) => {
    return downloadRegistryRef.current?.handoff(download) ?? false;
  }, []);
  useEffect(() => {
    // StrictMode replays effect setup/cleanup without remounting refs.  Give
    // each committed effect lifetime its own registry so the replay cannot
    // leave the live component pointing at an already-closed registry.
    const registry = new BrowserDownloadRegistry();
    downloadRegistryRef.current = registry;
    const cleanup = () => registry.close();
    window.addEventListener("pagehide", cleanup);
    return () => {
      window.removeEventListener("pagehide", cleanup);
      cleanup();
      if (downloadRegistryRef.current === registry) {
        downloadRegistryRef.current = null;
      }
    };
  }, []);

  const authoritativeShop = useAuthoritativeShop();
  const authenticatedShopId = authoritativeShop.shopId;
  const authenticatedShopGeneration = authoritativeShop.shopGeneration;
  const archivesQuery = useInfiniteQuery({
    queryKey: [
      "stocky-migration",
      "source-archives",
      authenticatedShopGeneration ?? "unresolved",
    ],
    queryFn: ({ pageParam }) => listStockySourceArchives(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled:
      authenticatedShopId !== null && authenticatedShopGeneration !== null,
  });
  const datasetsQuery = useQuery({
    queryKey: [
      "stocky-migration",
      "portability-datasets",
      authenticatedShopGeneration ?? "unresolved",
    ],
    queryFn: listStockyPortabilityDatasets,
    enabled:
      authenticatedShopId !== null && authenticatedShopGeneration !== null,
  });
  const [category, setCategory] = useState<string>(ARCHIVE_CATEGORIES[0].value);
  const context = t("指定なし");
  const [occurrences, setOccurrences] = useState<StockyArchiveOccurrence[]>([]);
  const archiveOnly = false;
  const [showExternalBackup, setShowExternalBackup] = useState(
    externalBackupInitiallyOpen,
  );
  const selectedCategoryGuide =
    ARCHIVE_CATEGORY_GUIDE[
      category as keyof typeof ARCHIVE_CATEGORY_GUIDE
    ] ?? null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [datasetProgress, setDatasetProgress] = useState<
    Record<string, DatasetProgress>
  >({});
  const [genericRowProgress, setGenericRowProgress] = useState<
    Record<string, GenericRowProgress>
  >({});
  const [genericVerificationActiveIds, setGenericVerificationActiveIds] =
    useState<ReadonlySet<string>>(() => new Set());
  const [datasetVerificationActiveKeys, setDatasetVerificationActiveKeys] =
    useState<ReadonlySet<string>>(() => new Set());
  const [verificationQueueEpoch, setVerificationQueueEpoch] = useState(0);
  const progressShopIdRef = useRef<string | null>(null);
  const progressShopGenerationRef = useRef<string | null>(null);
  const portabilityVerificationOwnerRef =
    useRef<PortabilityVerificationOwner | null>(null);
  const verificationRetryRequiredRef = useRef(new Set<string>());

  useEffect(() => {
    if (authoritativeShop.isLoading) return;
    const shopId = authoritativeShop.shopId;
    const shopGeneration = authoritativeShop.shopGeneration;
    if (
      typeof shopId !== "string" ||
      !isStockyPublicBigIntId(shopId) ||
      shopGeneration === null
    ) {
      progressShopIdRef.current = null;
      progressShopGenerationRef.current = null;
      portabilityVerificationOwnerRef.current = null;
      verificationRetryRequiredRef.current.clear();
      setDatasetVerificationActiveKeys((current) =>
        current.size === 0 ? current : new Set(),
      );
      setGenericVerificationActiveIds((current) =>
        current.size === 0 ? current : new Set(),
      );
      setGenericRowProgress({});
      setDatasetProgress({});
      return;
    }
    const normalizedShopId = shopId;
    const shopChanged =
      progressShopIdRef.current !== normalizedShopId ||
      progressShopGenerationRef.current !== shopGeneration;
    if (shopChanged) {
      progressShopIdRef.current = normalizedShopId;
      progressShopGenerationRef.current = shopGeneration;
      portabilityVerificationOwnerRef.current = null;
      verificationRetryRequiredRef.current.clear();
      setDatasetVerificationActiveKeys((current) =>
        current.size === 0 ? current : new Set(),
      );
      setGenericVerificationActiveIds((current) =>
        current.size === 0 ? current : new Set(),
      );
    }
    const archives =
      archivesQuery.data?.pages.flatMap((page) => page.archives) ?? [];
    setGenericRowProgress((current) => {
      let changed = false;
      const base = shopChanged ? {} : current;
      const next = { ...base };
      for (const archive of archives) {
        removeStoredGenericRowProgressForOtherSource(normalizedShopId, archive);
        const existing = base[archive.id];
        if (existing?.sourceSha256 === archive.sha256) continue;
        const loaded = loadStoredGenericRowProgress(normalizedShopId, archive);
        if (loaded === null) {
          if (existing !== undefined) {
            delete next[archive.id];
            changed = true;
          }
          continue;
        }
        next[archive.id] = loaded;
        changed = true;
      }
      return changed || shopChanged ? next : current;
    });
    const datasets = datasetsQuery.data?.datasets ?? [];
    setDatasetProgress((current) => {
      let changed = false;
      const base = shopChanged ? {} : current;
      const next = { ...base };
      for (const dataset of datasets) {
        if (base[dataset.dataset] !== undefined) continue;
        const loaded = loadStoredDatasetProgress(
          normalizedShopId,
          dataset.dataset,
        );
        if (loaded !== null) {
          next[dataset.dataset] = loaded;
          changed = true;
        }
      }
      return changed || shopChanged ? next : current;
    });
  }, [
    archivesQuery.data?.pages,
    authoritativeShop.isLoading,
    authoritativeShop.shopGeneration,
    authoritativeShop.shopId,
    datasetsQuery.data?.datasets,
  ]);

  const verifyDatasetProgress = useCallback(
    (
      dataset: StockyPortabilityDataset,
      progress: DatasetProgress,
      shopId: string,
      shopGeneration: string,
      manualRetry = false,
    ): boolean => {
      const retryKey = `dataset:${shopGeneration}:${dataset.dataset}:${progress.cursor ?? ""}:${progress.orderedRoot ?? ""}`;
      if (
        !progress.completeCandidate ||
        progress.verified ||
        progress.cursor === null ||
        portabilityVerificationOwnerRef.current !== null ||
        (!manualRetry && verificationRetryRequiredRef.current.has(retryKey))
      ) {
        return false;
      }
      verificationRetryRequiredRef.current.delete(retryKey);
      const owner: PortabilityVerificationOwner = {
        token: Symbol("dataset verification"),
        kind: "dataset",
        resourceKey: dataset.dataset,
        shopId,
        shopGeneration,
        cursor: progress.cursor,
        orderedRoot: progress.orderedRoot,
        sourceSha256: null,
      };
      portabilityVerificationOwnerRef.current = owner;
      setDatasetVerificationActiveKeys((current) =>
        new Set(current).add(dataset.dataset),
      );
      setError(null);
      let advanceQueue = false;
      const isCurrentOwner = () =>
        portabilityVerificationOwnerRef.current === owner &&
        progressShopIdRef.current === shopId &&
        progressShopGenerationRef.current === shopGeneration;
      void verifyStockyPortabilityDataset(dataset.dataset, progress.cursor)
        .then((verified) => {
          if (!isCurrentOwner()) return;
          if (
            verified.dataset !== dataset.dataset ||
            verified.high_watermark !== progress.highWatermark ||
            verified.record_count !== progress.recordCount ||
            verified.ordered_root !== progress.orderedRoot
          ) {
            throw localPortabilityIntegrityError(
              "データセットの再開検証結果が一致しません。",
            );
          }
          advanceQueue = true;
          verificationRetryRequiredRef.current.delete(retryKey);
          setDatasetProgress((all) => {
            const latest = all[dataset.dataset];
            if (
              latest?.cursor !== owner.cursor ||
              latest.orderedRoot !== owner.orderedRoot
            ) {
              return all;
            }
            return {
              ...all,
              [dataset.dataset]: { ...latest, verified: true },
            };
          });
          setNotice(
            t(
              "{dataset} はサーバー上の全 {count} 件・順序付きハッシュの整合性を検証しました。ブラウザが保存を完了したかは確認できないため、ダウンロード一覧で {files} を確認してください。",
              {
                dataset: dataset.key,
                count: verified.record_count,
                files: progress.filenames.join(" / "),
              },
            ),
          );
        })
        .catch((err: unknown) => {
          if (!isCurrentOwner()) return;
          if (err instanceof ApiError && [409, 502].includes(err.status)) {
            advanceQueue = true;
            verificationRetryRequiredRef.current.delete(retryKey);
            removeStoredDatasetProgress(shopId, dataset.dataset);
            setDatasetProgress((all) => ({
              ...all,
              [dataset.dataset]: emptyDatasetProgress(),
            }));
          } else {
            verificationRetryRequiredRef.current.add(retryKey);
          }
          setError(portabilityDownloadError(err));
        })
        .finally(() => {
          if (portabilityVerificationOwnerRef.current !== owner) return;
          portabilityVerificationOwnerRef.current = null;
          setDatasetVerificationActiveKeys((current) => {
            if (!current.has(dataset.dataset)) return current;
            const next = new Set(current);
            next.delete(dataset.dataset);
            return next;
          });
          if (advanceQueue) setVerificationQueueEpoch((value) => value + 1);
        });
      return true;
    },
    [],
  );

  const verifyGenericRowProgress = useCallback(
    (
      archive: StockySourceArchive,
      progress: GenericRowProgress,
      shopId: string,
      shopGeneration: string,
      manualRetry = false,
    ) => {
      const archiveId = archive.id;
      const retryKey = `generic:${shopGeneration}:${archiveId}:${archive.sha256}:${progress.cursor ?? ""}`;
      if (
        !progress.completeCandidate ||
        progress.verified ||
        progress.cursor === null ||
        portabilityVerificationOwnerRef.current !== null ||
        (!manualRetry && verificationRetryRequiredRef.current.has(retryKey))
      ) {
        return false;
      }
      verificationRetryRequiredRef.current.delete(retryKey);
      const owner: PortabilityVerificationOwner = {
        token: Symbol("generic verification"),
        kind: "generic",
        resourceKey: String(archiveId),
        shopId,
        shopGeneration,
        cursor: progress.cursor,
        orderedRoot: progress.orderedRoot,
        sourceSha256: archive.sha256,
      };
      portabilityVerificationOwnerRef.current = owner;
      setGenericVerificationActiveIds((current) =>
        new Set(current).add(archiveId),
      );
      setError(null);
      let advanceQueue = false;
      const isCurrentOwner = () =>
        portabilityVerificationOwnerRef.current === owner &&
        progressShopIdRef.current === shopId &&
        progressShopGenerationRef.current === shopGeneration;
      void verifyStockySourceGenericRows(archive, progress.cursor)
        .then((verified) => {
          if (!isCurrentOwner()) return;
          if (
            verified.high_watermark !== progress.highWatermark ||
            verified.record_count !== progress.recordCount ||
            verified.part_count !== progress.partCount ||
            verified.ordered_root !== progress.orderedRoot
          ) {
            throw localPortabilityIntegrityError("汎用行の再開検証結果が一致しません。");
          }
          advanceQueue = true;
          verificationRetryRequiredRef.current.delete(retryKey);
          setGenericRowProgress((all) => {
            const latest = all[archiveId];
            if (
              latest?.cursor !== owner.cursor ||
              latest.sourceSha256 !== owner.sourceSha256 ||
              latest.orderedRoot !== owner.orderedRoot
            ) {
              return all;
            }
            return {
              ...all,
              [archiveId]: { ...latest, verified: true },
            };
          });
          setNotice(
            t(
              "{filename} は全 {count} 行・{parts} パートのサーバー整合性を再検証しました。前回までを含む {files} をブラウザのダウンロード一覧で確認してください。",
              {
                filename: archive.filename,
                count: verified.record_count,
                parts: verified.part_count,
                files: progress.filenames.join(" / "),
              },
            ),
          );
        })
        .catch((err: unknown) => {
          if (!isCurrentOwner()) return;
          if (err instanceof ApiError && [409, 502].includes(err.status)) {
            advanceQueue = true;
            verificationRetryRequiredRef.current.delete(retryKey);
            removeStoredGenericRowProgress(shopId, archive);
            setGenericRowProgress((all) => ({
              ...all,
              [archiveId]: emptyGenericRowProgress(),
            }));
          } else {
            verificationRetryRequiredRef.current.add(retryKey);
          }
          setError(portabilityDownloadError(err));
        })
        .finally(() => {
          if (portabilityVerificationOwnerRef.current !== owner) return;
          portabilityVerificationOwnerRef.current = null;
          setGenericVerificationActiveIds((current) => {
            if (!current.has(archiveId)) return current;
            const next = new Set(current);
            next.delete(archiveId);
            return next;
          });
          if (advanceQueue) setVerificationQueueEpoch((value) => value + 1);
        });
      return true;
    },
    [],
  );

  useEffect(() => {
    if (portabilityVerificationOwnerRef.current !== null) return;
    const shopId = authoritativeShop.shopId;
    const shopGeneration = authoritativeShop.shopGeneration;
    if (
      typeof shopId !== "string" ||
      !isStockyPublicBigIntId(shopId) ||
      shopGeneration === null
    ) {
      return;
    }
    const normalizedShopId = shopId;
    const datasetsByKey = new Map(
      (datasetsQuery.data?.datasets ?? []).map((dataset) => [
        dataset.dataset,
        dataset,
      ]),
    );
    for (const datasetKey of Object.keys(datasetProgress).sort()) {
      const dataset = datasetsByKey.get(datasetKey);
      const progress = datasetProgress[datasetKey];
      if (
        dataset !== undefined &&
        verifyDatasetProgress(
          dataset,
          progress,
          normalizedShopId,
          shopGeneration,
        )
      ) {
        return;
      }
    }
    const archives =
      archivesQuery.data?.pages.flatMap((page) => page.archives) ?? [];
    const archivesById = new Map(
      archives.map((archive) => [archive.id, archive]),
    );
    for (const rawArchiveId of Object.keys(genericRowProgress).sort(
      compareCanonicalBigIntStrings,
    )) {
      const archive = archivesById.get(rawArchiveId);
      if (
        archive !== undefined &&
        verifyGenericRowProgress(
          archive,
          genericRowProgress[rawArchiveId],
          normalizedShopId,
          shopGeneration,
        )
      ) {
        return;
      }
    }
  }, [
    archivesQuery.data?.pages,
    authoritativeShop.shopGeneration,
    authoritativeShop.shopId,
    datasetProgress,
    datasetsQuery.data?.datasets,
    genericRowProgress,
    verificationQueueEpoch,
    verifyDatasetProgress,
    verifyGenericRowProgress,
  ]);

  const archiveUsage = archivesQuery.data?.pages[0]?.usage;
  const archiveLimits = archivesQuery.data?.pages[0]?.limits;
  const selectedFileBytes = occurrences.reduce(
    (total, occurrence) => total + occurrence.file.size,
    0,
  );
  const fileUploadExceedsShopQuota = Boolean(
    archiveUsage &&
    (selectedFileBytes > archiveUsage.remaining_raw_bytes ||
      occurrences.length > archiveUsage.remaining_files),
  );
  const archiveUsageProgress =
    archiveUsage && archiveLimits && archiveLimits.max_shop_bytes > 0
      ? Math.min(
          100,
          (archiveUsage.used_raw_bytes / archiveLimits.max_shop_bytes) * 100,
        )
      : 0;

  // Let the server auto-detect standard exports and retain the exact source
  // even when normalization cannot proceed.
  const parseOptions = null;

  const resetOccurrenceIds = useCallback(
    () =>
      setOccurrences((current) =>
        current.map(({ file }) => newOccurrence(file)),
      ),
    [],
  );

  const resetAllOccurrenceIds = useCallback(() => {
    resetOccurrenceIds();
    setNotice(null);
  }, [resetOccurrenceIds]);

  const changeCategory = useCallback(
    (value: string) => {
      setCategory(value);
      resetAllOccurrenceIds();
      setNotice(null);
    },
    [resetAllOccurrenceIds],
  );

  const upload = useCallback(async () => {
    if (
      !category ||
      !context.trim() ||
      occurrences.length === 0 ||
      fileUploadExceedsShopQuota
    )
      return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      let duplicates = 0;
      for (const occurrence of occurrences) {
        const saved = await uploadStockySourceArchive({
          occurrence,
          category,
          context: context.trim(),
          parseOptions,
          archiveOnly,
        });
        if (saved.already_archived || saved.duplicate_content) duplicates += 1;
      }
      setNotice(
        duplicates > 0
          ? t(
              "{files} ファイルを確認しました。同じ内容または同じ取込IDの原本 {duplicates} 件も一覧で確認できます。",
              {
                files: occurrences.length,
                duplicates,
              },
            )
          : t("{files} ファイルの原本を保存しました。", {
              files: occurrences.length,
            }),
      );
      await archivesQuery.refetch();
    } catch (err) {
      // 選択中の occurrence UUID は保持し、応答消失後も同一IDで安全に再試行する。
      setError(csvErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [
    archiveOnly,
    archivesQuery,
    category,
    context,
    fileUploadExceedsShopQuota,
    occurrences,
    parseOptions,
  ]);

  const downloadZip = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      saveBrowserDownload(await downloadStockyPortabilityZip());
    } catch (err) {
      setError(portabilityDownloadError(err));
    } finally {
      setBusy(false);
    }
  }, [saveBrowserDownload]);

  const downloadDatasetChunk = useCallback(
    async (dataset: StockyPortabilityDataset) => {
      const shopId = authenticatedShopId;
      const shopGeneration = authenticatedShopGeneration;
      if (
        typeof shopId !== "string" ||
        !isStockyPublicBigIntId(shopId) ||
        shopGeneration === null
      ) {
        setError(t("現在のショップを確認できません。再読み込みしてください。"));
        return;
      }
      let current = datasetProgress[dataset.dataset] ?? emptyDatasetProgress();
      if (
        current.expiresAt !== null &&
        current.expiresAt <= Math.floor(Date.now() / 1000)
      ) {
        removeStoredDatasetProgress(shopId, dataset.dataset);
        current = emptyDatasetProgress();
        setDatasetProgress((all) => ({
          ...all,
          [dataset.dataset]: current,
        }));
      }
      if (current.verified) {
        if (!current.browserFilesConfirmed) {
          setDatasetProgress((all) => ({
            ...all,
            [dataset.dataset]: { ...current, browserFilesConfirmed: true },
          }));
          setNotice(
            t(
              "{dataset}: ブラウザのダウンロード一覧で {files} を確認しました。再開情報をブラウザから削除しました。",
              { dataset: dataset.key, files: current.filenames.join(" / ") },
            ),
          );
          removeStoredDatasetProgress(shopId, dataset.dataset);
          return;
        }
        setDatasetProgress((all) => ({
          ...all,
          [dataset.dataset]: emptyDatasetProgress(),
        }));
        removeStoredDatasetProgress(shopId, dataset.dataset);
        setNotice(
          t("{dataset} を最初から再取得できる状態に戻しました。", {
            dataset: dataset.key,
          }),
        );
        return;
      }
      if (current.completeCandidate) {
        verifyDatasetProgress(
          dataset,
          current,
          shopId,
          shopGeneration,
          true,
        );
        return;
      }
      setBusy(true);
      setError(null);
      try {
        const chunk = await downloadStockyPortabilityDatasetChunk(
          dataset.dataset,
          current.cursor,
        );
        if (
          current.chunkCount > 0 &&
          (chunk.partSequence !== current.chunkCount + 1 ||
            chunk.cumulativeRecordCount < current.recordCount ||
            chunk.highWatermark !== current.highWatermark)
        ) {
          throw localPortabilityIntegrityError(
            "データセットチャンクの連続性を確認できません。",
          );
        }
        if (current.chunkCount === 0 && chunk.partSequence !== 1) {
          throw localPortabilityIntegrityError(
            "データセットの最初のチャンクを確認できません。",
          );
        }
        if (!saveBrowserDownload(chunk)) return;
        const next: DatasetProgress = {
          cursor: chunk.nextCursor,
          chunkCount: chunk.partSequence,
          recordCount: chunk.cumulativeRecordCount,
          highWatermark: chunk.highWatermark,
          verified: false,
          completeCandidate: chunk.completeCandidate,
          browserFilesConfirmed: false,
          filenames: [...current.filenames, chunk.filename],
          orderedRoot: chunk.orderedRoot,
          expiresAt: chunk.expiresAt,
        };
        persistDatasetProgress(shopId, dataset.dataset, next);
        setDatasetProgress((all) => ({ ...all, [dataset.dataset]: next }));
      } catch (err) {
        if (err instanceof ApiError && [409, 502].includes(err.status)) {
          removeStoredDatasetProgress(shopId, dataset.dataset);
          setDatasetProgress((all) => ({
            ...all,
            [dataset.dataset]: {
              ...emptyDatasetProgress(),
            },
          }));
        }
        setError(portabilityDownloadError(err));
      } finally {
        setBusy(false);
      }
    },
    [
      authenticatedShopGeneration,
      authenticatedShopId,
      datasetProgress,
      saveBrowserDownload,
      verifyDatasetProgress,
    ],
  );

  const downloadGenericRowChunk = useCallback(
    async (archive: StockySourceArchive) => {
      const shopId = authenticatedShopId;
      const shopGeneration = authenticatedShopGeneration;
      if (
        typeof shopId !== "string" ||
        !isStockyPublicBigIntId(shopId) ||
        shopGeneration === null
      ) {
        setError(t("現在のショップを確認できません。再読み込みしてください。"));
        return;
      }
      let current = genericRowProgress[archive.id] ?? emptyGenericRowProgress();
      if (
        current.expiresAt !== null &&
        current.expiresAt <= Math.floor(Date.now() / 1000)
      ) {
        removeStoredGenericRowProgress(shopId, archive);
        current = emptyGenericRowProgress();
        setGenericRowProgress((all) => ({
          ...all,
          [archive.id]: current,
        }));
      }
      if (current.verified) {
        if (!current.browserFilesConfirmed) {
          setGenericRowProgress((all) => ({
            ...all,
            [archive.id]: { ...current, browserFilesConfirmed: true },
          }));
          removeStoredGenericRowProgress(shopId, archive);
          setNotice(
            t(
              "{filename}: 前回までを含む {files} をブラウザのダウンロード一覧で確認しました。再開情報をブラウザから削除しました。",
              {
                filename: archive.filename,
                files: current.filenames.join(" / "),
              },
            ),
          );
          return;
        }
        setGenericRowProgress((all) => ({
          ...all,
          [archive.id]: emptyGenericRowProgress(),
        }));
        removeStoredGenericRowProgress(shopId, archive);
        setNotice(
          t("{filename} の汎用行を最初から再取得できる状態に戻しました。", {
            filename: archive.filename,
          }),
        );
        return;
      }
      if (current.completeCandidate) {
        verifyGenericRowProgress(
          archive,
          current,
          shopId,
          shopGeneration,
          true,
        );
        return;
      }

      setBusy(true);
      setError(null);
      try {
        const chunk = await downloadStockySourceGenericRowsChunk(
          archive,
          current.cursor,
        );
        if (
          current.partCount > 0 &&
          (chunk.partSequence !== current.partCount + 1 ||
            chunk.cumulativeRecordCount < current.recordCount ||
            chunk.highWatermark !== current.highWatermark)
        ) {
          throw localPortabilityIntegrityError("汎用行チャンクの連続性を確認できません。");
        }
        if (current.partCount === 0 && chunk.partSequence !== 1) {
          throw localPortabilityIntegrityError("汎用行の最初のチャンクを確認できません。");
        }
        if (!saveBrowserDownload(chunk)) return;
        const next: GenericRowProgress = {
          sourceSha256: archive.sha256,
          cursor: chunk.nextCursor,
          partCount: chunk.partSequence,
          recordCount: chunk.cumulativeRecordCount,
          highWatermark: chunk.highWatermark,
          verified: false,
          completeCandidate: chunk.completeCandidate,
          browserFilesConfirmed: false,
          filenames: [...current.filenames, chunk.filename],
          orderedRoot: chunk.orderedRoot,
          expiresAt: chunk.expiresAt,
        };
        // Persist before final verification so a reload between the browser
        // download handoff and verify can revalidate the signed final cursor.
        persistGenericRowProgress(shopId, archive, next);
        setGenericRowProgress((all) => ({ ...all, [archive.id]: next }));
      } catch (err) {
        if (err instanceof ApiError && [409, 502].includes(err.status)) {
          removeStoredGenericRowProgress(shopId, archive);
          setGenericRowProgress((all) => ({
            ...all,
            [archive.id]: emptyGenericRowProgress(),
          }));
        }
        setError(portabilityDownloadError(err));
      } finally {
        setBusy(false);
      }
    },
    [
      authenticatedShopGeneration,
      authenticatedShopId,
      genericRowProgress,
      saveBrowserDownload,
      verifyGenericRowProgress,
    ],
  );

  const verificationQueueActive =
    datasetVerificationActiveKeys.size > 0 ||
    genericVerificationActiveIds.size > 0;

  const rows = useMemo(
    () =>
      (archivesQuery.data?.pages.flatMap((page) => page.archives) ?? []).map(
        (archive) => {
          const progress =
            genericRowProgress[archive.id] ?? emptyGenericRowProgress();
          const verificationInFlight = genericVerificationActiveIds.has(
            archive.id,
          );
          return [
            archive.filename,
            archive.category,
            archive.generic_chunk_available
              ? t("表データを確認できます")
              : t("原本のみ"),
            archive.row_count === null
              ? "—"
              : formatNumber(archive.row_count),
            formatDateTime(archive.created_at),
            <InlineStack key={archive.id} gap="200" wrap>
              <Button
                variant="plain"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    saveBrowserDownload(
                      await downloadStockySourceArchive(archive),
                    );
                  } catch (err) {
                    setError(csvErrorMessage(err));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {t("原本をダウンロード")}
              </Button>
              {!archive.generic_chunk_available ? (
                <Text as="span" variant="bodySm" tone="subdued">
                  {genericUnavailableLabel(
                    archive.generic_chunk_unavailable_reason,
                  )}
                </Text>
              ) : (
                <BlockStack gap="100">
                  <Text as="span" variant="bodySm" tone="subdued">
                    {genericRowProgressLabel(progress)}
                  </Text>
                  <Button
                    variant="plain"
                    disabled={busy || verificationQueueActive}
                    loading={busy || verificationInFlight}
                    onClick={() => void downloadGenericRowChunk(archive)}
                  >
                    {genericRowButtonLabel(progress, verificationInFlight)}
                  </Button>
                </BlockStack>
              )}
            </InlineStack>,
          ];
        },
      ),
    [
      archivesQuery.data?.pages,
      busy,
      downloadGenericRowChunk,
      genericVerificationActiveIds,
      genericRowProgress,
      saveBrowserDownload,
      verificationQueueActive,
    ],
  );

  const datasetRows = useMemo(
    () =>
      (datasetsQuery.data?.datasets ?? []).map((dataset) => {
        const progress =
          datasetProgress[dataset.dataset] ?? emptyDatasetProgress();
        const verificationInFlight = datasetVerificationActiveKeys.has(
          dataset.dataset,
        );
        return [
          dataset.key,
          progress.browserFilesConfirmed
            ? t(
                "サーバー整合性検証済み・ユーザー確認済み（{count} 件 / root {root} / {files}）",
                {
                  count: progress.recordCount,
                  root: progress.orderedRoot?.slice(0, 12) ?? "-",
                  files: progress.filenames.join(" / "),
                },
              )
            : progress.verified
              ? t(
                  "サーバー整合性検証済み・ブラウザ保存未確認（{count} 件 / root {root} / {files}）",
                  {
                    count: progress.recordCount,
                    root: progress.orderedRoot?.slice(0, 12) ?? "-",
                    files: progress.filenames.join(" / "),
                  },
                )
              : progress.completeCandidate && verificationInFlight
                ? t("全チャンク受信済み・サーバー最終検証中（{count} 件）", {
                    count: progress.recordCount,
                  })
                : progress.completeCandidate
                  ? t(
                      "全チャンク受信済み・サーバー最終検証待ち（{count} 件）",
                      {
                        count: progress.recordCount,
                      },
                    )
                  : progress.chunkCount > 0
                    ? t(
                        "{chunks} チャンク / {count} 件を受信・保存操作開始（保存未確認）",
                        {
                          chunks: progress.chunkCount,
                          count: progress.recordCount,
                        },
                      )
                    : t("未受信"),
          <Button
            key={dataset.dataset}
            variant="plain"
            disabled={busy || verificationQueueActive}
            loading={busy || verificationInFlight}
            onClick={() => void downloadDatasetChunk(dataset)}
          >
            {progress.browserFilesConfirmed
              ? t("最初から再取得")
              : progress.verified
                ? t("ダウンロード一覧でファイル名を確認した")
                : verificationInFlight
                  ? t("最終整合性を検証中")
                  : progress.completeCandidate
                    ? t("検証を再試行")
                    : progress.chunkCount > 0
                      ? t("次のチャンクを取得")
                      : t("最初のチャンクを取得")}
          </Button>,
        ];
      }),
    [
      busy,
      datasetProgress,
      datasetVerificationActiveKeys,
      datasetsQuery.data?.datasets,
      downloadDatasetChunk,
      verificationQueueActive,
    ],
  );

  return (
    <Card>
      <BlockStack gap="400">
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">
            {t("Stock on hand・CSVで書き出せるレポート原本")}
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            {t(
              "Stock on hand・Average costなど、StockyからダウンロードしたCSVを選びます。",
            )}
          </Text>
        </BlockStack>

        {archiveUsage && archiveLimits && (
          <BlockStack gap="100">
            <Text as="h3" variant="headingSm">
              {t("原本保管容量")}
            </Text>
            <ProgressBar progress={archiveUsageProgress} size="small" />
            <Text as="p" variant="bodySm" tone="subdued">
              {t(
                "{used} / {cap}、{files} / {fileCap} ファイルを使用中（残り {remaining}、{remainingFiles} ファイル）",
                {
                  used: formatBytes(archiveUsage.used_raw_bytes),
                  cap: formatBytes(archiveLimits.max_shop_bytes),
                  files: formatNumber(archiveUsage.file_count),
                  fileCap: formatNumber(archiveLimits.max_files_per_shop),
                  remaining: formatBytes(archiveUsage.remaining_raw_bytes),
                  remainingFiles: formatNumber(archiveUsage.remaining_files),
                },
              )}
            </Text>
          </BlockStack>
        )}

        {fileUploadExceedsShopQuota && (
          <Banner tone="critical" title={t("原本保管容量を超えます")}>
            <Text as="p">
              {t(
                "この選択内容は店舗の保管上限（512 MiB・20,000ファイル）を超えるため保存できません。ローカルの原本は削除せず保管し、移行期限が迫っている場合はサポートへ連絡してください。",
              )}
            </Text>
          </Banner>
        )}

        <Select
          label={t("保存する項目")}
          options={ARCHIVE_CATEGORIES.map((option) => ({
            value: option.value,
            label: t(option.label),
          }))}
          value={category}
          onChange={changeCategory}
          disabled={busy}
        />
        {selectedCategoryGuide && (
          <BlockStack gap="100">
            <Text as="p" variant="bodySm" tone="subdued">
              {t("Stockyでの取得場所")}: {t(selectedCategoryGuide.path)}
            </Text>
            <Text as="p" fontWeight="semibold">
              {t("選ぶファイル: {name}", {
                name: selectedCategoryGuide.file,
              })}
            </Text>
          </BlockStack>
        )}
        <input
          type="file"
          multiple
          accept=".csv,.tsv,.txt,text/csv,text/plain,text/tab-separated-values"
          aria-label={t("保管する Stocky 原本")}
          disabled={busy}
          onChange={(event) => {
            setOccurrences(
              Array.from(event.target.files ?? []).map(newOccurrence),
            );
            setNotice(null);
            setError(null);
          }}
        />
        {occurrences.map((occurrence) => (
          <Text
            key={occurrence.sourceImportId}
            as="p"
            variant="bodySm"
            tone="subdued"
          >
            {occurrence.file.name}
          </Text>
        ))}
        <InlineStack align="end">
          <Button
            variant="primary"
            loading={busy}
            disabled={
              busy ||
              !context.trim() ||
              occurrences.length === 0 ||
              fileUploadExceedsShopQuota
            }
            onClick={upload}
          >
            {t("原本を保管する")}
          </Button>
        </InlineStack>

        {notice && (
          <Banner tone="success" title={t("原本を確認しました")}>
            <Text as="p">{notice}</Text>
          </Banner>
        )}
        {error && (
          <Banner
            tone="critical"
            title={t("原本またはZIPを処理できませんでした")}
          >
            <Text as="p">{error}</Text>
          </Banner>
        )}

        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center" gap="200">
            <BlockStack gap="050">
              <Text as="h3" variant="headingSm">
                {t("保存したStockyファイルの一覧")}
              </Text>
              {archivesQuery.data && (
                <Text as="p" variant="bodySm" tone="subdued">
                  {t("全 {total} 件中 {loaded} 件を表示", {
                    total: archivesQuery.data.pages[0]?.total ?? rows.length,
                    loaded: rows.length,
                  })}
                </Text>
              )}
            </BlockStack>
          </InlineStack>
          {archivesQuery.isLoading ? (
            <Text as="p" tone="subdued">
              {t("保存済み原本を読み込んでいます…")}
            </Text>
          ) : archivesQuery.isError ? (
            <Banner
              tone="critical"
              title={t("保存済み原本を取得できませんでした")}
              action={{
                content: t("再試行"),
                onAction: () => void archivesQuery.refetch(),
              }}
            />
          ) : rows.length === 0 ? (
            <Text as="p" tone="subdued">
              {t("保存済みの原本はまだありません。")}
            </Text>
          ) : (
            <BlockStack gap="200">
              <DataTable
                columnContentTypes={["text", "text", "text", "numeric", "text", "text"]}
                headings={[
                  t("ファイル名"),
                  t("カテゴリ"),
                  t("正規化レベル"),
                  t("行数"),
                  t("保存日時"),
                  t("操作"),
                ]}
                rows={rows}
              />
              {archivesQuery.hasNextPage && (
                <InlineStack align="center">
                  <Button
                    loading={archivesQuery.isFetchingNextPage}
                    onClick={() => void archivesQuery.fetchNextPage()}
                  >
                    {t("次の100件を読み込む")}
                  </Button>
                </InlineStack>
              )}
            </BlockStack>
          )}
        </BlockStack>

        <BlockStack gap="200">
          <Button
            onClick={() => setShowExternalBackup((open) => !open)}
            ariaExpanded={showExternalBackup}
            ariaControls="stocky-external-backup"
          >
            {showExternalBackup
              ? t("外部バックアップを閉じる")
              : t("保存済みデータを外部バックアップする（通常は不要）")}
          </Button>
          <Collapsible
            open={showExternalBackup}
            id="stocky-external-backup"
            transition={{ duration: "150ms", timingFunction: "ease" }}
          >
            <BlockStack gap="300">
              <Banner
                tone="info"
                title={t("これはStockyからデータを取得する操作ではありません")}
              >
                <Text as="p">
                  {t(
                    "すでにUreyukiBoxへ保存したデータを、別の場所へバックアップするための機能です。Stocky側で未取得のデータは増えません。通常の移行確認は、上の「保存したStockyファイルの一覧」で行ってください。",
                  )}
                </Text>
              </Banner>
              <InlineStack align="end">
                <Button loading={busy} disabled={busy} onClick={downloadZip}>
                  {t("保存済みデータをZIPでダウンロード")}
                </Button>
              </InlineStack>
              <Banner
                tone="warning"
                title={t("ZIPに収まらない場合は分割ダウンロードします")}
              >
                <Text as="p">
                  {t(
                    "一括ZIPが上限を超えた場合は、下の各データセットと、一覧にある各ファイルのJSONLを順番に取得し、最後の検証が完了するまで続けてください。取得中に内容が変わった場合は最初からやり直す案内を表示します。ZIPに失敗しても、保存済みデータは削除・変更されません。",
                  )}
                </Text>
              </Banner>
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">
                  {t("保存済みデータをJSONLで分割ダウンロード")}
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {t(
                    "各ボタンで最大8MBずつダウンロードします。最終ファイルの後に件数と並び順を検証します。表示されたすべてのファイル名がブラウザのダウンロード一覧にあることを確認してください。",
                  )}
                </Text>
                {datasetsQuery.isLoading ? (
                  <Text as="p" tone="subdued">
                    {t("保存済みデータを読み込んでいます…")}
                  </Text>
                ) : datasetsQuery.isError ? (
                  <Banner
                    tone="critical"
                    title={t("保存済みデータを取得できませんでした")}
                    action={{
                      content: t("再試行"),
                      onAction: () => void datasetsQuery.refetch(),
                    }}
                  />
                ) : (
                  <DataTable
                    columnContentTypes={["text", "text", "text"]}
                    headings={[
                      t("データの種類"),
                      t("ダウンロード状況"),
                      t("操作"),
                    ]}
                    rows={datasetRows}
                  />
                )}
              </BlockStack>
            </BlockStack>
          </Collapsible>
        </BlockStack>
      </BlockStack>
    </Card>
  );
}
